import { Card, Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type LabelsModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/LabelsModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import LabelsModuleActions from './LabelsModuleUpdateDrawer';

export type LabelsModuleProps = { module: LabelsModuleWithPermissionsSpecsFragment };

const LabelsModulePage = ({ module }: LabelsModuleProps) => {
    const { t } = useTranslation(['labelsModule', 'common']);
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('labelsModule:content.displayName')}>{module.displayName}</Descriptions.Item>
            <Descriptions.Item label={t('labelsModule:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<LabelsModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('labelsModule:title', { name: module.displayName }) : module.displayName}
        >
            <Card>
                <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label={t('labelsModule:displayName')}>{module.displayName}</Descriptions.Item>
                </Descriptions>
            </Card>
        </ConsolePageWithHeader>
    );
};

export default LabelsModulePage;
